/**
 * Page d'Accès Rapide
 * 
 * Cette page offre un accès tactile simple et rapide aux fonctionnalités principales :
 * - Interface tactile optimisée
 * - Accès direct aux fonctionnalités essentielles
 * - Design responsive et moderne
 * - Alternative simple à la sidebar
 * 
 * @component
 * @returns {JSX.Element} Composant QuickAccess
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { aside as frAside } from '../../../locales/fr/aside';
import { aside as arAside } from '../../../locales/ar/aside';
import { aside as esAside } from '../../../locales/es/aside';
import useMyAppStore from '../../../stores/myAppStore';
import { getAllowedRoutes } from '../../../services/AppDataServices/config/activity-routes.config';

const translations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const QuickAccess = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const navigate = useNavigate();
  const { setPageTitle, currentActivity } = useMyAppStore();
  const [allowedRoutes, setAllowedRoutes] = useState([]);

  useEffect(() => {
    setPageTitle('Accès Rapide');
  }, [setPageTitle]);

  useEffect(() => {
    if (currentActivity) {
      const routes = getAllowedRoutes(currentActivity.id);
      setAllowedRoutes(routes);
    } else {
      setAllowedRoutes([]);
    }
  }, [currentActivity]);

  const quickActions = [
    {
      id: 'pos',
      icon: '🛒',
      label: t.routes.pos || 'Point de Vente',
      color: 'bg-blue-500 hover:bg-blue-600',
      description: 'Interface de caisse tactile'
    },
    {
      id: 'sales',
      icon: '📊',
      label: t.routes.sales || 'Ventes',
      color: 'bg-green-500 hover:bg-green-600',
      description: 'Gestion des ventes'
    },
    {
      id: 'products',
      icon: '📦',
      label: t.routes.products || 'Produits',
      color: 'bg-purple-500 hover:bg-purple-600',
      description: 'Catalogue des produits'
    },
    {
      id: 'customers',
      icon: '👥',
      label: t.routes.customers || 'Clients',
      color: 'bg-orange-500 hover:bg-orange-600',
      description: 'Base de données clients'
    },
    {
      id: 'dashboard',
      icon: '📈',
      label: t.routes.dashboard || 'Tableau de Bord',
      color: 'bg-indigo-500 hover:bg-indigo-600',
      description: 'Vue d\'ensemble'
    },
    {
      id: 'payments',
      icon: '💳',
      label: t.routes.payments || 'Paiements',
      color: 'bg-teal-500 hover:bg-teal-600',
      description: 'Historique des paiements'
    },
    {
      id: 'tables',
      icon: '🪑',
      label: t.routes.tables || 'Tables',
      color: 'bg-pink-500 hover:bg-pink-600',
      description: 'Gestion des tables',
      condition: () => currentActivity?.id === 'restaurant'
    },
    {
      id: 'appointments',
      icon: '📅',
      label: t.routes.appointments || 'Rendez-vous',
      color: 'bg-cyan-500 hover:bg-cyan-600',
      description: 'Calendrier des RDV',
      condition: () => ['coiffure', 'autoecole', 'reparation'].includes(currentActivity?.id)
    },
    {
      id: 'commandes',
      icon: '📋',
      label: t.routes.commandes || 'Commandes',
      color: 'bg-yellow-500 hover:bg-yellow-600',
      description: 'Gestion des commandes',
      condition: () => currentActivity?.id === 'atelier'
    },
    {
      id: 'approvisionnements',
      icon: '📦',
      label: t.routes.approvisionnements || 'Stock',
      color: 'bg-red-500 hover:bg-red-600',
      description: 'Gestion des stocks'
    },
    {
      id: 'settings',
      icon: '⚙️',
      label: t.routes.settings || 'Paramètres',
      color: 'bg-gray-500 hover:bg-gray-600',
      description: 'Configuration'
    },
    {
      id: 'activity-settings',
      icon: '🏢',
      label: t.routes.activitySettings || 'Paramètres Activité',
      color: 'bg-emerald-500 hover:bg-emerald-600',
      description: 'Configuration de l\'activité'
    }
  ];

  const handleActionClick = (actionId) => {
    navigate(`/${lang}/myapp/${actionId}`);
  };

  const filteredActions = quickActions.filter(action => {
    // Vérifier si l'action est autorisée pour l'activité actuelle
    const isAllowed = allowedRoutes.includes(action.id);
    
    // Vérifier les conditions spécifiques
    if (action.condition) {
      return isAllowed && action.condition();
    }
    
    return isAllowed;
  });

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
          Accès Rapide
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Interface tactile pour un accès rapide aux fonctionnalités
        </p>
        {currentActivity && (
          <div className="mt-4 inline-block px-4 py-2 bg-indigo-100 dark:bg-indigo-900 rounded-full">
            <span className="text-indigo-800 dark:text-indigo-200 font-medium">
              {currentActivity.name}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredActions.map((action) => (
          <button
            key={action.id}
            onClick={() => handleActionClick(action.id)}
            className={`
              ${action.color}
              text-white rounded-xl p-6 shadow-lg hover:shadow-xl
              transform hover:scale-105 transition-all duration-200
              flex flex-col items-center justify-center
              min-h-[120px] relative overflow-hidden
            `}
          >
            {/* Effet de brillance au survol */}
            <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-200" />
            
            {/* Icône */}
            <div className="text-4xl mb-3">
              {action.icon}
            </div>
            
            {/* Label */}
            <div className="text-center">
              <div className="font-semibold text-sm leading-tight">
                {action.label}
              </div>
              <div className="text-xs opacity-90 mt-1">
                {action.description}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Section d'aide */}
      <div className="mt-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
          💡 Conseils d'utilisation
        </h2>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div>
            <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Navigation rapide
            </h3>
            <ul className="space-y-1">
              <li>• Cliquez sur une carte pour accéder directement à la fonctionnalité</li>
              <li>• Les cartes s'adaptent à votre activité actuelle</li>
              <li>• Interface optimisée pour écrans tactiles</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Fonctionnalités
            </h3>
            <ul className="space-y-1">
              <li>• Accès instantané aux outils principaux</li>
              <li>• Design responsive pour tous les écrans</li>
              <li>• Animations fluides et feedback visuel</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAccess; 